import { useState } from 'react';
import { Button, Select, SelectItem, Switch } from '@heroui/react';
import { useMailboxesList } from '@/features/mailboxes/hooks';
import { useUpdateDomain } from '@/features/domains/hooks';
import type { Domain } from '@/features/domains/types';

/**
 * Catch-all with its target. Mail for unknown addresses goes to one active mailbox of this
 * organisation; the server refuses anything else (outside addresses, suspended mailboxes,
 * unverified domains), so the switch only shows "on" when mail is really routed.
 */
export function CatchAllSetting({ domain, canWrite }: { domain: Domain; canWrite: boolean }) {
  const update = useUpdateDomain(domain.id);
  const mailboxes = useMailboxesList({ page: 1, pageSize: 200, status: 'active' });
  const [choosing, setChoosing] = useState(false);
  const [target, setTarget] = useState<string>('');
  const active = (mailboxes.data?.data ?? []).filter((m) => m.status === 'active');
  const verified = domain.verified;

  if (domain.catchAllEnabled) {
    return (
      <div className="flex items-center gap-3">
        <span className="text-sm text-fg-muted">To <b className="text-fg">{domain.catchAllTarget}</b></span>
        <Switch isSelected isDisabled={!canWrite || update.isPending} aria-label="Turn off catch-all"
          onValueChange={(v) => { if (!v) update.mutate({ catchAllEnabled: false }); }} />
      </div>
    );
  }
  if (!choosing) {
    return (
      <Switch isSelected={false} isDisabled={!canWrite || !verified || update.isPending}
        aria-label={verified ? 'Turn on catch-all' : 'Catch-all is available once the domain is verified'}
        onValueChange={(v) => { if (v) setChoosing(true); }} />
    );
  }
  return (
    <div className="flex w-full max-w-md items-center gap-2">
      <Select aria-label="Catch-all mailbox" size="sm" variant="bordered" placeholder={active.length ? 'Choose a mailbox' : 'Create a mailbox first'}
        isDisabled={!active.length} selectedKeys={target ? new Set([target]) : new Set()}
        onSelectionChange={(keys) => setTarget(String(Array.from(keys)[0] ?? ''))}>
        {active.map((m) => <SelectItem key={m.address}>{m.address}</SelectItem>)}
      </Select>
      <Button size="sm" color="primary" isDisabled={!target} isLoading={update.isPending}
        onPress={() => update.mutate({ catchAllEnabled: true, catchAllTarget: target }, { onSuccess: () => setChoosing(false) })}>Turn on</Button>
      <Button size="sm" variant="light" onPress={() => { setChoosing(false); setTarget(''); }}>Cancel</Button>
    </div>
  );
}
